import React from 'react';
import Image from 'frontastic/lib/image';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import {
    mediumDesktop,
    tablet,
    hugeDesktop,
    largeDesktop,
    semiLargeDesktop,
} from 'helpers/utils/screensizes';
import Button from 'components/commercetools-ui/atoms/button';
import Link from 'components/commercetools-ui/atoms/link';
import Typography from 'components/commercetools-ui/atoms/typography';
import Markdown from 'components/commercetools-ui/organisms/markdown';
import Slider from 'components/commercetools-ui/atoms/slider';
import { StaticImageData } from 'next/image';

const ImageBanner = (props: any) => {
    const { content: sectionWithImageText } = props;
    const imageDescription = sectionWithImageText?.description;
    const descriptionArray: any[] = [];
    descriptionArray?.push(imageDescription);

    const handleDownloadPDF = () => {
        const pdfUrl = sectionWithImageText?.ctas[0]?.button?.href;
        const link = document.createElement('a');
        link.href = pdfUrl;
        link.download = 'your-filename.pdf';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="relative  left-full  w-screen -translate-x-full lg:grid lg:grid-cols-12">
            <div
                id="advice-section"
                className={`flex items-center  lg:col-span-12 lg:px-0 ${sectionWithImageText?.desktop_image_alignment === 'Left' ? 'md:flex-row' : 'lg:flex-row-reverse'
                    } ${sectionWithImageText?.mobile_image_alignment === 'Top' ? 'flex-col' : 'flex-col'}`}
            >
                <div className="middleresolution:h-[37rem]  relative h-[20rem] w-full md:h-[46rem] lg:flex-1  lg:flex-grow xl:h-[32rem] 2xl:h-[45rem] ">
                    {sectionWithImageText?.image?.url && (
                        <Image
                            priority
                            loading="eager"
                            src={sectionWithImageText?.image?.url}
                            alt={sectionWithImageText?.image?.description}
                            fill
                            style={{ objectFit: 'cover' }}
                        />
                    )}
                </div>
                <div className="flex-1 flex-grow pb-48 lg:px-48 2xl:px-144">
                    {descriptionArray?.map((field: any) => (
                        <div key={field?.categoryId}>
                            <Markdown
                                markdown={field}
                                className="image-container px-24 text-center text-16 font-normal leading-6 text-[#333] md:text-18  lg:leading-8 xl:text-16 2xl:text-20 [&>div>img]:mb-10"
                            />
                        </div>
                    ))}
                    {sectionWithImageText?.ctas?.[0]?.button && (
                        <div className="px-24 ">
                            <Link link={sectionWithImageText?.ctas?.[0]?.button?.href?.href}>
                                <Button
                                    onClick={handleDownloadPDF}
                                    className="m-auto mt-48 block w-full rounded-none border-2 border-[#2B2C39] bg-white px-18 py-16 shadow-sm hover:!border-[#F4F4F4] hover:!bg-[#F4F4F4] md:px-32 md:py-16 lg:w-fit"
                                >
                                    <Typography
                                        as="span"
                                        className="text-16 font-semibold leading-5  text-[#090909] lg:leading-6 xl:text-14 2xl:text-16"
                                    >
                                        {sectionWithImageText?.ctas?.[0]?.button?.title}
                                    </Typography>
                                </Button>
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const FullLengthVideo = (props: any) => {
    const { content: sectionWithVideo } = props;
    const [isHugeDesktopSize] = useMediaQuery(hugeDesktop);
    const [isSemiLargeDesktopSize] = useMediaQuery(semiLargeDesktop);
    const [ismediumDesktopSize] = useMediaQuery(mediumDesktop);
    const [isLargeDesktopSize] = useMediaQuery(largeDesktop);

    if (!sectionWithVideo) {
        return null;
    }

    return (
        <div className="">
            <div className="video-container flex justify-center md:px-100 lg:py-85 2xl:px-208 ">
                <iframe
                    src={sectionWithVideo?.video_link?.href}
                    width="100%"
                    loading="lazy"
                    height={
                        isHugeDesktopSize
                            ? '720px'
                            : isSemiLargeDesktopSize
                                ? '480px'
                                : ismediumDesktopSize
                                    ? '500px'
                                    : isLargeDesktopSize
                                        ? '600px'
                                        : '320px'
                    }
                ></iframe>
            </div>
        </div>
    )
};

const MultiImageCarousel = (props: any) => {
    const { section_with_images: sectionWithMultiImages } = props?.content ?? {};
    const [isTabletSize] = useMediaQuery(tablet);

    if (!sectionWithMultiImages) {
        return null;
    }

    return (
        <div className="hero-slider-container w-full">
            <Slider
                slidesPerView={isTabletSize ? 1.3 : 1}
                dots={true}
                spaceBetween={16}
                arrows={isTabletSize}
                innerArrows
                allowArrowsOnTouchDevice
                allowTouchMove={true}
                prevButtonStyles={{ padding: '15px', left: '0px' }}
                nextButtonStyles={{ padding: '15px', right: '0px' }}
                autoplay={{
                    delay: 2000,
                    disableOnInteraction: false,
                }}
            >
                {sectionWithMultiImages?.image?.map(
                    (
                        slide: { url: string | StaticImageData | undefined; title: string | undefined },
                        index: string | number | null | undefined,
                    ) => (
                        <div key={index} className="relative h-[18rem] md:h-[34rem] 2xl:h-[44rem]">
                            {slide?.url && (
                                <Image loading="lazy" src={slide.url} alt={slide.title} fill style={{ objectFit: 'cover' }} />
                            )}
                        </div>
                    ),
                )}
            </Slider>
        </div>
    )
};

export {
    ImageBanner,
    FullLengthVideo,
    MultiImageCarousel
}
